import type { ErrorLogEntry } from '../services/error-log.service';

/**
 * Spiega in italiano una voce del Log Errori: cosa è successo, cosa significa,
 * cosa fare. Il server scrive codici e messaggi per sviluppatori; qui si
 * traducono per chi amministra. Un codice sconosciuto non rompe niente: ricade
 * sul messaggio originale, marcato come errore.
 */

export type Severity = 'info' | 'warning' | 'error';

export interface Fact {
  label: string;
  value: string;
  /** Link interno al pannello (scheda utente, scheda chiamata). */
  href?: string;
  mono?: boolean;
}

export interface ErrorDescription {
  severity: Severity;
  sourceLabel: string;
  title: string;
  meaning: string;
  action: string;
  facts: Fact[];
}

type Ctx = Record<string, unknown>;
type EntryLike = Pick<ErrorLogEntry, 'source' | 'code' | 'message' | 'context'>;

export const SOURCE_LABELS: Record<string, string> = {
  'sms-relay': 'Gateway SMS',
  settings: 'Impostazioni',
  voice: 'Sveglia Twilio',
  sms: 'SMS (OVH)',
  email: 'Email',
  call: 'Chiamate',
  client: 'Browser e app degli utenti',
  bonus: 'Bonus',
  presence: 'Presenza consulenti',
  auth: 'Accessi',
  'marketing-consent-export': 'Consensi marketing',
};

/** Fonti mostrate come filtro, nell'ordine in cui compaiono. */
export const SOURCE_FILTERS = ['settings', 'sms-relay', 'voice', 'sms', 'email', 'call', 'client', 'bonus', 'presence', 'auth'];

export const SEVERITY_LABELS: Record<Severity, string> = {
  info: 'Informazione',
  warning: 'Attenzione',
  error: 'Errore',
};

const PURPOSE_LABELS: Record<string, string> = {
  otp: 'codice di verifica',
  call: 'avviso di chiamata',
  notification: 'notifica',
  marketing: 'marketing',
  test: 'prova',
};

/** Errori che l'app Android del telefono gateway segnala per un invio fallito. */
const RELAY_SEND_ERRORS: Record<string, { meaning: string; action: string }> = {
  NO_SERVICE: { meaning: 'Il telefono non aveva campo mentre mandava l\'SMS.', action: 'Controlla il segnale del telefono gateway. Il server riprova da solo, fino a 3 volte.' },
  RADIO_OFF: { meaning: 'La radio del telefono è spenta (per esempio modalità aereo).', action: 'Disattiva la modalità aereo sul telefono gateway.' },
  GENERIC_FAILURE: { meaning: 'Errore generico del telefono o della rete.', action: 'Se si ripete, riavvia l\'app del gateway.' },
  NULL_PDU: { meaning: 'Numero o testo non validi: il telefono non ha potuto comporre l\'SMS.', action: 'Controlla il numero del destinatario. Non si riprova.' },
  LIMIT_EXCEEDED: { meaning: 'Android ha bloccato l\'invio per troppi SMS in poco tempo.', action: 'Aspetta qualche minuto: il telefono ne manda al massimo 100 all\'ora.' },
  SIM_ABSENT: { meaning: 'Il telefono non vede la SIM.', action: 'Controlla che la SIM sia inserita.' },
  SEND_TIMEOUT: { meaning: 'Il telefono non ha confermato l\'invio entro il tempo massimo.', action: 'Controlla la rete del telefono; il messaggio può passare a OVH.' },
  LEASE_EXPIRED: { meaning: 'Il telefono ha preso il messaggio ma non ha mai confermato l\'invio: app bloccata o chiusa.', action: 'Riavvia l\'app e controlla lo stato in Gateway SMS.' },
  UNKNOWN_INTERRUPTED: { meaning: 'L\'app si è interrotta mentre inviava: non sappiamo se l\'SMS sia partito, e l\'app non lo rimanda mai due volte.', action: 'Chiedi al destinatario se l\'ha ricevuto.' },
  EXPIRED_ON_DEVICE: { meaning: 'Il messaggio era già scaduto quando il telefono l\'ha preso.', action: 'Se capita spesso, il telefono sta prendendo i messaggi troppo tardi: controlla la connessione.' },
  NO_PERMISSION: { meaning: 'L\'app non ha il permesso di mandare SMS.', action: 'Riconcedi il permesso SMS nelle impostazioni del telefono.' },
  RELAY_SEND_FAILED: { meaning: 'Il telefono ha riferito un errore di invio senza specificare il motivo.', action: 'Guarda i dettagli tecnici e prova a rimandare l\'SMS da Gateway SMS.' },
};

const TWILIO_STATUS: Record<string, { severity: Severity; title: string; meaning: string; action: string }> = {
  completed: { severity: 'info', title: 'Sveglia risposta', meaning: 'Il consulente ha risposto alla telefonata di sveglia.', action: 'Niente da fare.' },
  'no-answer': { severity: 'warning', title: 'Sveglia senza risposta', meaning: 'Il consulente non ha risposto alla telefonata di sveglia.', action: 'Resta l\'SMS col link. Se succede sempre con lo stesso consulente, controlla il suo numero.' },
  busy: { severity: 'warning', title: 'Sveglia: numero occupato', meaning: 'Il numero del consulente era occupato.', action: 'Niente: il consulente può comunque aprire il link ricevuto per SMS.' },
  canceled: { severity: 'info', title: 'Sveglia annullata', meaning: 'La telefonata di sveglia è stata annullata prima che squillasse.', action: 'Niente da fare.' },
  failed: { severity: 'error', title: 'Sveglia non riuscita', meaning: 'Twilio non è riuscito a fare la chiamata (numero non valido o problema dell\'account).', action: 'Controlla il numero del consulente e l\'account Twilio.' },
  ringing: { severity: 'info', title: 'Sveglia: stava squillando', meaning: 'Lo stato è stato registrato mentre il telefono squillava. Non è un errore e non è l\'esito finale.', action: 'Guarda la chiamata nella scheda chiamata: dice se il consulente ha risposto o rifiutato.' },
  'in-progress': { severity: 'info', title: 'Sveglia in corso', meaning: 'La telefonata di sveglia era in corso quando è stata registrata.', action: 'Niente da fare.' },
  queued: { severity: 'info', title: 'Sveglia in attesa', meaning: 'Twilio aveva preso in carico la richiesta ma la chiamata non era ancora partita.', action: 'Niente da fare.' },
};

const CLIENT_CODES: Record<string, { severity: Severity; title: string; meaning: string; action: string }> = {
  REJECTED: { severity: 'warning', title: 'Il browser non tiene acceso lo schermo', meaning: 'Durante una chiamata il sito chiede al browser di non spegnere lo schermo, e il browser ha detto no (di solito per il risparmio energetico). La chiamata funziona lo stesso: si usa un sistema di riserva.', action: 'Niente da fare, salvo che l\'utente segnali audio che si interrompe a schermo spento: in quel caso disattivi il risparmio energetico.' },
  UNSUPPORTED: { severity: 'info', title: 'Il browser non sa tenere acceso lo schermo', meaning: 'Il browser è vecchio o limitato e non ha la funzione per tenere acceso lo schermo durante la chiamata. Si usa un sistema di riserva.', action: 'Niente da fare. Se l\'audio si interrompe, consiglia all\'utente Chrome o Safari aggiornati.' },
  AUTOPLAY_BLOCKED: { severity: 'warning', title: 'Il browser ha bloccato l\'audio', meaning: 'Il browser dell\'utente ha impedito di far partire l\'audio da solo.', action: 'Guarda dispositivo e browser nei dettagli: se si ripete sullo stesso modello, segnalalo allo sviluppo.' },
  AUTOPLAY_BLOCKED_PREFLIGHT: { severity: 'warning', title: 'Audio bloccato prima della chiamata', meaning: 'Il controllo iniziale ha trovato l\'audio bloccato dal browser.', action: 'Guarda dispositivo e browser nei dettagli.' },
  CALL_MIC_MUTED_MANUAL: { severity: 'info', title: 'Microfono spento durante la chiamata', meaning: 'L\'utente ha spento il microfono durante la chiamata.', action: 'Niente da fare: è una scelta dell\'utente.' },
  CALL_MIC_SUSPENDED: { severity: 'warning', title: 'Microfono sospeso durante la chiamata', meaning: 'Il microfono si è fermato durante la chiamata senza che l\'utente lo spegnesse.', action: 'Guarda il dispositivo nei dettagli: se si ripete sullo stesso modello, segnalalo allo sviluppo.' },
  CALL_MIC_TRACK_ENDED: { severity: 'warning', title: 'Microfono interrotto durante la chiamata', meaning: 'La traccia audio del microfono si è interrotta durante la chiamata.', action: 'Guarda il dispositivo nei dettagli.' },
  CALL_RECORDING_EMPTY: { severity: 'warning', title: 'Registrazione della chiamata vuota', meaning: 'Il browser ha prodotto una registrazione senza audio.', action: 'Guarda la chiamata e il dispositivo.' },
  CALL_RECORDING_FAILED: { severity: 'error', title: 'Registrazione della chiamata non riuscita', meaning: 'Il browser non è riuscito a registrare l\'audio della chiamata.', action: 'Guarda dispositivo e browser nei dettagli.' },
  CALL_RECORDING_UPLOAD_FAILED: { severity: 'error', title: 'Caricamento della registrazione non riuscito', meaning: 'La registrazione è stata fatta ma non è arrivata al server.', action: 'Controlla se il server l\'ha recuperata (vedi «Registrazione parziale»).' },
};

const SMS_OVH_CODES: Record<string, { severity: Severity; title: string; meaning: string; action: string }> = {
  SMS_SEND_FAILED: { severity: 'error', title: 'OVH non è riuscito a mandare l\'SMS', meaning: 'Il servizio SMS di OVH ha risposto con un errore.', action: 'Controlla il credito e lo stato del servizio nel pannello OVH, poi rimanda l\'SMS.' },
  SMS_RECEIVER_REJECTED: { severity: 'error', title: 'OVH ha rifiutato il numero', meaning: 'OVH non accetta il numero di destinazione.', action: 'Controlla il numero del destinatario.' },
  SMS_NOT_CONFIGURED: { severity: 'error', title: 'Servizio SMS non configurato', meaning: 'Mancano le credenziali o il nome del servizio OVH nelle impostazioni.', action: 'Controlla Impostazioni → SMS.' },
  SMS_DISABLED: { severity: 'warning', title: 'Invio SMS disattivato', meaning: 'L\'invio SMS è spento nelle impostazioni.', action: 'Riattivalo in Impostazioni → SMS se non è voluto.' },
  INVALID_PHONE: { severity: 'error', title: 'Numero di telefono non valido', meaning: 'Il numero non è in formato internazionale.', action: 'Correggi il numero dell\'utente.' },
  EMPTY_SMS_BODY: { severity: 'error', title: 'SMS senza testo', meaning: 'Il messaggio da inviare era vuoto.', action: 'Segnala allo sviluppo: è un errore del sistema.' },
};

const MODE_LABELS: Record<string, string> = {
  ovh: 'solo OVH',
  relay: 'solo telefono',
  'relay+ovh': 'telefono + OVH',
};

/* ---------------------------------------------------------------- helpers */

const str = (c: Ctx, k: string): string | null => {
  const v = c[k];
  return typeof v === 'string' && v.trim() ? v : null;
};
const numOf = (c: Ctx, k: string): number | null => (typeof c[k] === 'number' && Number.isFinite(c[k] as number) ? (c[k] as number) : null);
const boolOf = (c: Ctx, k: string): boolean | null => (typeof c[k] === 'boolean' ? (c[k] as boolean) : null);
const shortId = (id: string): string => (id.length > 8 ? `…${id.slice(-8)}` : id);

function f(label: string, value: string | number | null | undefined, extra: Partial<Fact> = {}): Fact | null {
  if (value === null || value === undefined || value === '') return null;
  return { label, value: String(value), ...extra };
}
const compact = (facts: Array<Fact | null>): Fact[] => facts.filter((x): x is Fact => x !== null);

const userLink = (id: string | null) => (id ? { href: `/dashboard/admin/users/${id}` } : {});
const callLink = (id: string | null) => (id ? { href: `/dashboard/admin/calls/${id}` } : {});
const seconds = (n: number | null) => (n === null ? null : `${n} ${n === 1 ? 'secondo' : 'secondi'}`);
const yesNo = (b: boolean | null) => (b === null ? null : b ? 'sì' : 'no');
const purposeOf = (c: Ctx, message: string): string | null => {
  const direct = str(c, 'purpose');
  const fromMsg = /\((otp|call|notification|marketing|test)\)/.exec(message)?.[1] ?? null;
  const key = direct ?? fromMsg;
  return key ? (PURPOSE_LABELS[key] ?? key) : null;
};

/* ------------------------------------------------------------------ main */

export function describeError(entry: EntryLike): ErrorDescription {
  const ctx = (entry.context ?? {}) as Ctx;
  const code = entry.code ?? '';
  const sourceLabel = SOURCE_LABELS[entry.source] ?? entry.source;
  const base = { sourceLabel };

  // --- Modifiche fatte da un admin (middleware/auditChanges lato API)
  if (code === 'ADMIN_CHANGE') {
    const email = str(ctx, 'email') ?? 'un amministratore';
    const path = str(ctx, 'path') ?? '';
    const mode = str(ctx, 'mode');
    const fields = Array.isArray(ctx.fields) ? (ctx.fields as unknown[]).filter((x): x is string => typeof x === 'string') : [];
    return {
      ...base, severity: 'info',
      title: `${email} ha modificato: ${sourceLabel}`,
      meaning: `Modifica salvata da ${email}${mode ? `: modalità gateway impostata su «${MODE_LABELS[mode] ?? mode}»` : ''}. Sono registrati solo i nomi dei campi, mai i valori.`,
      action: 'Niente da fare, se la modifica era voluta. Altrimenti chiedi spiegazioni a chi l\'ha fatta.',
      facts: compact([
        f('Chi', email),
        f('Ruolo', str(ctx, 'role')),
        f('Modalità gateway', mode ? (MODE_LABELS[mode] ?? mode) : null),
        f('Campi modificati', fields.length ? fields.join(', ') : null, { mono: true }),
        f('Azione', `${str(ctx, 'method') ?? ''} ${path}`.trim(), { mono: true }),
        f('IP', str(ctx, 'ip'), { mono: true }),
      ]),
    };
  }

  // --- Sveglia Twilio
  if (code === 'VOICE_CALL_OUTCOME') {
    const status = str(ctx, 'status') ?? 'unknown';
    const dur = numOf(ctx, 'durationSeconds');
    const hung = boolOf(ctx, 'hungUpBySystem');
    const t = TWILIO_STATUS[status] ?? { severity: 'warning' as Severity, title: `Sveglia: esito «${status}»`, meaning: 'Esito della telefonata di sveglia non riconosciuto.', action: 'Guarda i dettagli tecnici.' };
    const { severity, action } = t;
    let { title, meaning } = t;
    if (status === 'completed') {
      if (hung) {
        title = 'Sveglia interrotta: il consulente è entrato nell\'app';
        meaning = 'Il consulente ha aperto l\'app mentre la sveglia squillava, quindi il sistema ha chiuso la telefonata.';
      } else if (dur === 0) {
        title = 'Sveglia conclusa subito';
        meaning = 'La telefonata risulta conclusa dopo 0 secondi: il consulente potrebbe aver rifiutato o riattaccato subito.';
      } else {
        title = `Sveglia risposta${dur !== null ? ` (${dur} s)` : ''}`;
        meaning = `Il consulente ha risposto alla telefonata di sveglia${dur !== null ? ` e ha ascoltato il messaggio per ${seconds(dur)}` : ''}.`;
      }
    }
    const pro = str(ctx, 'professionalUserId');
    return {
      ...base, severity, title, meaning, action,
      facts: compact([
        f('Consulente', pro ? `Scheda consulente (${shortId(pro)})` : null, { ...userLink(pro) }),
        f('Numero chiamato', str(ctx, 'to'), { mono: true }),
        f('Chiamata', str(ctx, 'callSessionId') ? `Scheda chiamata (${shortId(str(ctx, 'callSessionId')!)})` : null, { ...callLink(str(ctx, 'callSessionId')) }),
        f('Stato Twilio', status, { mono: true }),
        f('Durata', seconds(dur)),
        f('Chiusa dal sistema', yesNo(hung)),
        f('Codice Twilio (SID)', str(ctx, 'sid'), { mono: true }),
      ]),
    };
  }
  if (code === 'VOICE_CALL_FAILED') {
    const auth = /HTTP 40[13]/.test(entry.message);
    return {
      ...base, severity: 'error',
      title: 'La telefonata di sveglia non è partita',
      meaning: auth
        ? 'Twilio ha rifiutato la richiesta per un problema di autorizzazione: credenziali non valide o account non ancora verificato.'
        : 'Twilio non ha accettato la richiesta di chiamata.',
      action: 'Controlla in Impostazioni → Twilio Voice le credenziali, il numero mittente e il saldo; poi fai «Testa chiamata».',
      facts: compact([
        f('Numero chiamato', str(ctx, 'to'), { mono: true }),
        f('Chiamata', str(ctx, 'callSessionId') ? `Scheda chiamata (${shortId(str(ctx, 'callSessionId')!)})` : null, { ...callLink(str(ctx, 'callSessionId')) }),
        f('Risposta di Twilio', entry.message),
      ]),
    };
  }

  // --- Gateway SMS (telefono Android)
  if (code === 'RELAY_DEVICES_SILENT') {
    return {
      ...base, severity: 'error',
      title: 'Il telefono gateway non si fa sentire',
      meaning: 'Da più di 3 minuti nessun contatto dal telefono, e ci sono SMS in coda.',
      action: 'Apri l\'app sul telefono: deve dire «Service attivo · Connesso». Se è bloccata, tocca Ferma e poi Avvia. Intanto gli SMS passano da OVH.',
      facts: compact([f('Telefoni attivi', numOf(ctx, 'activeDevices')), f('SMS in coda', numOf(ctx, 'queueDepth'))]),
    };
  }
  if (code === 'RELAY_QUEUE_BACKLOG') {
    return {
      ...base, severity: 'warning',
      title: 'Troppi SMS in attesa nella coda',
      meaning: 'La coda degli SMS del telefono gateway è sopra la soglia: il telefono non riesce a smaltirla.',
      action: 'Controlla che il telefono sia connesso e abbia campo; valuta la modalità «Solo OVH» finché non si svuota.',
      facts: compact([f('SMS in coda', numOf(ctx, 'queueDepth'))]),
    };
  }
  if (code === 'RELAY_MESSAGE_EXPIRED') {
    const p = purposeOf(ctx, entry.message);
    return {
      ...base, severity: 'warning',
      title: `Un SMS (${p ?? 'messaggio'}) è scaduto senza partire`,
      meaning: 'L\'SMS è rimasto in coda troppo a lungo e non serviva più: un codice dopo 5 minuti, un link di chiamata dopo 90 secondi.',
      action: 'Se era un codice, l\'utente deve richiederne uno nuovo. Se capita spesso, controlla che il telefono sia connesso.',
      facts: compact([f('Destinatario', str(ctx, 'to'), { mono: true }), f('Tipo', p)]),
    };
  }
  if (code === 'DEVICE_BATTERY_LOW') {
    return {
      ...base, severity: 'warning',
      title: `Batteria del telefono gateway bassa (${numOf(ctx, 'battery') ?? '?'}%)`,
      meaning: `Il telefono è sotto la soglia impostata${numOf(ctx, 'threshold') !== null ? ` (${numOf(ctx, 'threshold')}%)` : ''}${boolOf(ctx, 'charging') === false ? ' e non è in carica' : ''}.`,
      action: 'Controlla il caricatore. Con il telefono sempre in carica non dovrebbe succedere.',
      facts: compact([f('Telefono', str(ctx, 'device')), f('Batteria', numOf(ctx, 'battery') !== null ? `${numOf(ctx, 'battery')}%` : null), f('In carica', yesNo(boolOf(ctx, 'charging')))]),
    };
  }
  if (entry.source === 'sms-relay' && RELAY_SEND_ERRORS[code]) {
    const e = RELAY_SEND_ERRORS[code]!;
    const p = purposeOf(ctx, entry.message);
    return {
      ...base, severity: 'error',
      title: `Invio SMS fallito${p ? ` (${p})` : ''}`,
      meaning: e.meaning, action: e.action,
      facts: compact([
        f('Destinatario', str(ctx, 'to'), { mono: true }), f('Telefono', str(ctx, 'device')),
        f('Tentativi', numOf(ctx, 'attempts')), f('Motivo originale', entry.message),
      ]),
    };
  }

  // --- SMS OVH
  if (code === 'INTL_SMS_VOLUME') {
    return {
      ...base, severity: 'warning',
      title: 'Molti SMS verso l\'estero questo mese',
      meaning: 'Il numero di SMS verso numeri stranieri ha superato la soglia di avviso: la SIM del telefono ha gli SMS illimitati solo verso l\'Italia.',
      action: 'Guarda quali paesi in Gateway SMS → SMS internazionali, e controlla che non siano registrazioni sospette.',
      facts: compact([f('Dettaglio', entry.message)]),
    };
  }
  if (entry.source === 'sms' && SMS_OVH_CODES[code]) {
    const e = SMS_OVH_CODES[code]!;
    return { ...base, ...e, facts: compact([f('Destinatario', str(ctx, 'to'), { mono: true }), f('Messaggio originale', entry.message)]) };
  }

  // --- Email
  if (entry.source === 'email') {
    const missing = code === 'TEMPLATE_MISSING';
    return {
      ...base, severity: 'error',
      title: missing ? 'Modello email mancante' : 'Email non inviata',
      meaning: missing ? 'Il modello di email richiesto non esiste o è disattivato, quindi non è partito niente.' : 'Il server di posta ha rifiutato o non ha accettato l\'email.',
      action: missing ? 'Controlla Template Email: il modello indicato deve esistere ed essere attivo.' : 'Controlla le impostazioni SMTP in Servizi Esterni e l\'indirizzo del destinatario.',
      facts: compact([f('Destinatario', str(ctx, 'to')), f('Modello', str(ctx, 'templateSlug'), { mono: true }), f('Motivo originale', entry.message)]),
    };
  }

  // --- Chiamate
  if (code === 'CALL_RECORDING_MISSING' || code === 'CALL_RECORDING_PARTIAL_ONLY') {
    const missing = code === 'CALL_RECORDING_MISSING';
    const cs = str(ctx, 'callSessionId');
    return {
      ...base, severity: 'warning',
      title: missing ? 'Chiamata senza registrazione audio' : 'Registrazione recuperata solo in parte',
      meaning: missing
        ? 'La chiamata si è conclusa senza che arrivasse nessuna registrazione, quindi la trascrizione non è disponibile.'
        : 'L\'upload finale non è arrivato: è stato recuperato il pezzo caricato durante la chiamata, ma può mancare la parte finale.',
      action: 'Apri la scheda chiamata e controlla dispositivo e motivo di fine. Se si ripete sullo stesso modello, segnalalo allo sviluppo.',
      facts: compact([
        f('Chiamata', cs ? `Scheda chiamata (${shortId(cs)})` : null, { ...callLink(cs) }), f('Tipo', str(ctx, 'callType')),
        f('Durata', seconds(numOf(ctx, 'durationSeconds'))), f('Motivo di fine', str(ctx, 'endReason'), { mono: true }),
        f('Dati recuperati', numOf(ctx, 'recoveredBytes') !== null ? `${numOf(ctx, 'recoveredBytes')} byte` : null),
        f('Dispositivo cliente', str(ctx, 'callerUserAgent')), f('Dispositivo consulente', str(ctx, 'professionalUserAgent')),
      ]),
    };
  }
  if (code === 'CALL_ERROR') {
    return {
      ...base, severity: 'error',
      title: 'Errore del sistema durante una chiamata',
      meaning: 'Qualcosa di inatteso è andato storto nel server mentre gestiva una chiamata (non una condizione normale come il credito finito).',
      action: 'Guarda la fase e il messaggio nei dettagli; se si ripete, segnalalo allo sviluppo.',
      facts: compact([f('Fase', str(ctx, 'phase'), { mono: true }), f('Messaggio', entry.message)]),
    };
  }
  if (code === 'CALL_RELEASE_FALLBACK_OFFLINE') {
    return {
      ...base, severity: 'warning',
      title: 'Consulente messo offline dopo una chiamata',
      meaning: 'Alla fine di una chiamata il sistema non è riuscito a rimettere il consulente online, e per sicurezza lo ha messo offline.',
      action: 'Il consulente deve ripassare online dal suo pannello. Se si ripete, segnalalo allo sviluppo.',
      facts: compact([f('Chiamata', str(ctx, 'callSessionId') ? `Scheda chiamata (${shortId(str(ctx, 'callSessionId')!)})` : null, { ...callLink(str(ctx, 'callSessionId')) }), f('Errore', str(ctx, 'error'))]),
    };
  }

  // --- Bonus, accessi, esportazioni
  if (code === 'BONUS_JOB_GRANT_FAILED' || code === 'BONUS_JOB_EMAIL_FAILED') {
    const grant = code === 'BONUS_JOB_GRANT_FAILED';
    const uid = str(ctx, 'userId');
    return {
      ...base, severity: 'error',
      title: entry.message,
      meaning: grant ? 'Il bonus non è stato accreditato al cliente.' : 'Il bonus è stato accreditato ma l\'email di avviso al cliente non è partita.',
      action: grant ? 'Apri la scheda utente e controlla il wallet; l\'accredito va fatto a mano.' : 'Controlla l\'invio email; il bonus è già a posto.',
      facts: compact([f('Utente', uid ? `Scheda utente (${shortId(uid)})` : null, { ...userLink(uid) }), f('Lavoro', str(ctx, 'job'), { mono: true })]),
    };
  }
  if (code === 'MASTER_PASSWORD_LOGIN' || code === 'MASTER_PASSWORD_DENIED') {
    const ok = code === 'MASTER_PASSWORD_LOGIN';
    return {
      ...base, severity: 'warning',
      title: ok ? 'Accesso con la password passe-partout' : 'Tentativo con la password passe-partout rifiutato',
      meaning: ok ? 'Qualcuno è entrato in un account di prova con la password speciale.' : 'È stata provata la password speciale su un account dove non è ammessa.',
      action: ok ? 'Se non eri tu o un collega, verifica chi ha la password.' : 'Se non eri tu o un collega, verifica chi ha provato.',
      facts: compact([f('Account', str(ctx, 'email')), f('Ambiente', str(ctx, 'environment'))]),
    };
  }
  if (entry.source === 'marketing-consent-export') {
    return { ...base, severity: 'info', title: 'Esportazione dei consensi marketing', meaning: 'Un amministratore ha scaricato il file CSV dei consensi marketing.', action: 'Niente da fare: è un registro di controllo.', facts: compact([f('Dettaglio', entry.message)]) };
  }

  // --- Browser e app degli utenti
  if (entry.source === 'client' && CLIENT_CODES[code]) {
    const e = CLIENT_CODES[code]!;
    return { ...base, ...e, facts: compact([f('Utente', str(ctx, 'userName') ?? str(ctx, 'userEmail'), userLink(str(ctx, 'userId'))), f('Ruolo', str(ctx, 'role')), f('Messaggio originale', entry.message)]) };
  }

  // --- Sconosciuto: si ricade sul messaggio, senza nascondere niente
  return {
    ...base, severity: 'error',
    title: entry.message.length > 140 ? `${entry.message.slice(0, 137)}…` : entry.message,
    meaning: 'Questo errore non ha ancora una spiegazione in italiano.',
    action: 'Guarda i dettagli tecnici e il codice; se si ripete, segnalalo allo sviluppo.',
    facts: compact([f('Codice', code || null, { mono: true })]),
  };
}

/** "3 minuti fa", "2 ore fa", "ieri". Assoluto sempre disponibile a parte. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'adesso';
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'} fa`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'ieri' : `${d} giorni fa`;
}
